import { Plus, X } from "lucide-react";
import { normalizarHex } from "../datos/marcaApi";
import type { ColorMarca } from "../datos/marcaApi";

/**
 * Edición de la paleta de la marca (tab Visual): cambiar el hex de cada
 * color, quitarlo, o agregar uno nuevo. Vive aparte de `MarcaVisualPanel`
 * para no pasarnos del tamaño de archivo recomendado.
 *
 * Solo se edita el `hex`. `name` y `role` (que los escribe la IA en el
 * onboarding) se preservan tal cual y viajan intactos al guardado — igual que
 * en "Mi marca" → "Mi diseño" (`CompanyData.tsx`), que tampoco los deja
 * editar pero tampoco los pierde.
 *
 * El componente NO guarda: solo levanta el borrador. El guardado es explícito
 * y vive en `MarcaVisualPanel`.
 */

interface EditorColoresMarcaProps {
  colores: ColorMarca[];
  onCambiar: (colores: ColorMarca[]) => void;
  deshabilitado?: boolean;
}

/** El mismo color neutro con el que arranca un color nuevo en `CompanyData.tsx`. */
const HEX_NUEVO = "#888888";

export const EditorColoresMarca = ({
  colores,
  onCambiar,
  deshabilitado = false,
}: EditorColoresMarcaProps) => {
  const cambiarHex = (indice: number, hex: string) =>
    onCambiar(colores.map((c, i) => (i === indice ? { ...c, hex } : c)));

  const quitar = (indice: number) => onCambiar(colores.filter((_, i) => i !== indice));

  const agregar = () => onCambiar([...colores, { hex: HEX_NUEVO }]);

  return (
    <div className="mt-3 flex flex-wrap items-start gap-3">
      {colores.map((color, indice) => {
        const hexNormalizado = normalizarHex(color.hex);
        // `<input type="color">` solo acepta `#rrggbb`: con un `#fff` (válido
        // para nosotros y para `CompanyData`) o con algo a medio tipear, el
        // navegador ignora el valor y el picker queda desincronizado. Para el
        // picker mostramos el neutro; el valor real sigue siendo el del texto.
        const valorPicker =
          hexNormalizado && /^#[0-9a-f]{6}$/.test(hexNormalizado) ? hexNormalizado : HEX_NUEVO;
        return (
          <div
            key={indice}
            className="relative flex w-[108px] flex-col items-center gap-2 rounded-xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] p-3"
          >
            <button
              type="button"
              onClick={() => quitar(indice)}
              disabled={deshabilitado}
              aria-label={`Quitar el color ${color.hex}`}
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-[rgb(var(--brand-black-rgb)_/_0.1)] bg-white text-[rgb(var(--brand-black-rgb)_/_0.5)] shadow-sm transition-colors hover:text-brand-black disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>

            {/* El hex es un dato dinámico de la base: va SIEMPRE por estilo
                inline, nunca por clase de Tailwind. */}
            <label
              className="relative h-12 w-12 cursor-pointer rounded-full border border-[rgb(var(--brand-black-rgb)_/_0.1)] shadow-sm"
              style={{ backgroundColor: hexNormalizado ?? "transparent" }}
              title={hexNormalizado ? color.hex : "Color inválido"}
            >
              <input
                type="color"
                value={valorPicker}
                disabled={deshabilitado}
                onChange={(e) => cambiarHex(indice, e.target.value)}
                aria-label="Elegir color"
                className="absolute inset-0 h-full w-full cursor-pointer rounded-full opacity-0"
              />
            </label>

            <input
              type="text"
              // `?? ""` y no `color.hex` pelado: el `hex` viene de un JSON de
              // la base, así que puede faltar. Con `undefined` el input pasaría
              // a no-controlado y React tiraría el warning clásico.
              value={color.hex ?? ""}
              disabled={deshabilitado}
              onChange={(e) => cambiarHex(indice, e.target.value)}
              spellCheck={false}
              aria-label="Código hexadecimal del color"
              aria-invalid={!hexNormalizado}
              className={`w-full rounded-lg border py-1.5 px-2 text-center font-mono text-[11px] uppercase text-brand-black outline-none transition-colors focus:border-brand-primary ${
                hexNormalizado ? "border-[rgb(var(--brand-black-rgb)_/_0.12)]" : "border-[#C33B36] bg-[#FCF3F2]"
              }`}
            />
          </div>
        );
      })}

      <button
        type="button"
        onClick={agregar}
        disabled={deshabilitado}
        aria-label="Agregar un color"
        className="flex h-[108px] w-[108px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-[rgb(var(--brand-black-rgb)_/_0.2)] text-[rgb(var(--brand-black-rgb)_/_0.45)] transition-colors hover:border-brand-primary hover:text-brand-primary disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
        <span className="text-[11px]">Agregar</span>
      </button>
    </div>
  );
};

export default EditorColoresMarca;
